import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaPlus, FaEdit, FaTrash, FaUsers, FaUser } from 'react-icons/fa';
import AddressAutocomplete from '../components/AddressAutocomplete';
import './StaffManagement.css';

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [offices, setOffices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState(getEmptyFormData());

  function getEmptyFormData() {
    return {
      username: '',
      email: '',
      password: '',
      firstName: '',
      lastName: '',
      role: 'staff',
      phone: '',
      gender: '',
      ssn: '',
      dateOfBirth: '',
      agentLicenseNumber: '',
      suretyLicenseExpiration: '',
      hireDate: '',
      photoUrl: '',
      addressStreet: '',
      addressCity: '',
      addressState: 'MT',
      addressCounty: '',
      addressZip: '',
      driversLicense: '',
      driversLicenseState: 'MT',
      driversLicenseExpiration: '',
      hasAccessToAllOffices: false,
      memberType: '',
      officeIds: []
    };
  }

  useEffect(() => {
    fetchStaff();
    fetchOffices();
  }, []);

  const fetchStaff = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/users');
      setStaff(response.data.data);
    } catch (error) {
      console.error('Error fetching staff:', error);
      alert('Failed to load staff members');
    } finally {
      setLoading(false);
    }
  };

  const fetchOffices = async () => {
    try {
      const response = await axios.get('/users/offices/list');
      setOffices(response.data.data);
    } catch (error) {
      console.error('Error fetching offices:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleOfficeToggle = (officeId) => {
    setFormData(prev => ({
      ...prev,
      officeIds: prev.officeIds.includes(officeId)
        ? prev.officeIds.filter(id => id !== officeId)
        : [...prev.officeIds, officeId]
    }));
  };

  const handleAddressSelect = (addressData) => {
    setFormData(prev => ({
      ...prev,
      addressStreet: addressData.street || '',
      addressCity: addressData.city || '',
      addressState: addressData.state || 'MT',
      addressCounty: addressData.county || '',
      addressZip: addressData.zip || ''
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log('=== SUBMITTING FORM DATA ===');
    console.log('Form Data:', formData);
    console.log('Editing Staff ID:', editingStaff?.id);

    try {
      if (editingStaff) {
        // Update existing staff
        console.log('Sending PUT request to:', `/users/${editingStaff.id}`);
        const response = await axios.put(`/users/${editingStaff.id}`, formData);
        console.log('Server response:', response.data);
        alert('Staff member updated successfully!');
      } else {
        // Create new staff
        console.log('Sending POST request to: /users');
        const response = await axios.post('/users', formData);
        console.log('Server response:', response.data);
        alert('Staff member created successfully!');
      }

      setShowForm(false);
      setEditingStaff(null);
      setFormData(getEmptyFormData());
      fetchStaff();
    } catch (error) {
      console.error('Error saving staff:', error);
      console.error('Error response:', error.response?.data);
      alert(error.response?.data?.message || 'Failed to save staff member');
    }
  };

  const handleEdit = (staffMember) => {
    setEditingStaff(staffMember);
    setFormData({
      username: staffMember.username || '',
      email: staffMember.email || '',
      password: '', // Don't populate password for security
      firstName: staffMember.first_name || '',
      lastName: staffMember.last_name || '',
      role: staffMember.role || 'staff',
      phone: staffMember.phone || '',
      gender: staffMember.gender || '',
      ssn: staffMember.ssn || '',
      dateOfBirth: staffMember.date_of_birth ? staffMember.date_of_birth.split('T')[0] : '',
      agentLicenseNumber: staffMember.agent_license_number || '',
      suretyLicenseExpiration: staffMember.surety_license_expiration ? staffMember.surety_license_expiration.split('T')[0] : '',
      hireDate: staffMember.hire_date ? staffMember.hire_date.split('T')[0] : '',
      photoUrl: staffMember.photo_url || '',
      addressStreet: staffMember.address_street || '',
      addressCity: staffMember.address_city || '',
      addressState: staffMember.address_state || 'MT',
      addressCounty: staffMember.address_county || '',
      addressZip: staffMember.address_zip || '',
      driversLicense: staffMember.drivers_license || '',
      driversLicenseState: staffMember.drivers_license_state || 'MT',
      driversLicenseExpiration: staffMember.drivers_license_expiration ? staffMember.drivers_license_expiration.split('T')[0] : '',
      hasAccessToAllOffices: staffMember.has_access_to_all_offices || false,
      memberType: staffMember.member_type || '',
      officeIds: [] // Will need to fetch from backend
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to deactivate this staff member?')) {
      return;
    }

    try {
      await axios.delete(`/users/${id}`);
      alert('Staff member deactivated successfully');
      fetchStaff();
    } catch (error) {
      console.error('Error deleting staff:', error);
      alert('Failed to deactivate staff member');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingStaff(null);
    setFormData(getEmptyFormData());
  };

  if (loading) {
    return <div className="loading">Loading staff...</div>;
  }

  return (
    <div className="staff-management">
      <div className="staff-header">
        <h1><FaUsers /> Staff Management</h1>
        {!showForm && (
          <button className="btn-primary" onClick={() => setShowForm(true)}>
            <FaPlus /> Add Staff Member
          </button>
        )}
      </div>

      {showForm ? (
        <div className="staff-form-container">
          <div className="form-header">
            <h2>{editingStaff ? 'Edit Staff Member' : 'Add New Staff Member'}</h2>
            <button className="btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>

          <form onSubmit={handleSubmit} className="staff-form">
            {/* Login Information */}
            <section className="form-section">
              <h3>Login Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Username *</label>
                  <input
                    type="text"
                    value={formData.username}
                    onChange={(e) => handleInputChange('username', e.target.value)}
                    required
                    disabled={editingStaff} // Can't change username after creation
                  />
                </div>
                <div className="form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => handleInputChange('email', e.target.value)}
                    required
                  />
                </div>
              </div>
              {!editingStaff && (
                <div className="form-group">
                  <label>Password *</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => handleInputChange('password', e.target.value)}
                    required={!editingStaff}
                    placeholder="Enter initial password"
                  />
                </div>
              )}
            </section>

            {/* Personal Information */}
            <section className="form-section">
              <h3>Personal Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>First Name *</label>
                  <input
                    type="text"
                    value={formData.firstName}
                    onChange={(e) => handleInputChange('firstName', e.target.value)}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Last Name *</label>
                  <input
                    type="text"
                    value={formData.lastName}
                    onChange={(e) => handleInputChange('lastName', e.target.value)}
                    required
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Phone Number</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => handleInputChange('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                  />
                </div>
                <div className="form-group">
                  <label>Gender</label>
                  <select
                    value={formData.gender}
                    onChange={(e) => handleInputChange('gender', e.target.value)}
                  >
                    <option value="">Select Gender</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>S.S.N</label>
                  <input
                    type="text"
                    value={formData.ssn}
                    onChange={(e) => handleInputChange('ssn', e.target.value)}
                    placeholder="XXX-XX-XXXX"
                    maxLength="11"
                  />
                </div>
                <div className="form-group">
                  <label>Date Of Birth</label>
                  <input
                    type="date"
                    value={formData.dateOfBirth}
                    onChange={(e) => handleInputChange('dateOfBirth', e.target.value)}
                  />
                </div>
              </div>
            </section>

            {/* Employment Information */}
            <section className="form-section">
              <h3>Employment Information</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Member Type</label>
                  <select
                    value={formData.memberType}
                    onChange={(e) => handleInputChange('memberType', e.target.value)}
                  >
                    <option value="">Select Member Type</option>
                    <option value="full-time">Full-Time</option>
                    <option value="part-time">Part-Time</option>
                    <option value="contractor">Contractor</option>
                    <option value="intern">Intern</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Role *</label>
                  <select
                    value={formData.role}
                    onChange={(e) => handleInputChange('role', e.target.value)}
                    required
                  >
                    <option value="staff">Staff</option>
                    <option value="agent">Agent</option>
                    <option value="admin">Admin</option>
                    <option value="viewer">Viewer</option>
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Hire Date</label>
                  <input
                    type="date"
                    value={formData.hireDate}
                    onChange={(e) => handleInputChange('hireDate', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Agent License Number</label>
                  <input
                    type="text"
                    value={formData.agentLicenseNumber}
                    onChange={(e) => handleInputChange('agentLicenseNumber', e.target.value)}
                    placeholder="Enter agent license number"
                  />
                </div>
              </div>
              <div className="form-group">
                <label>Surety License Expiration Date</label>
                <input
                  type="date"
                  value={formData.suretyLicenseExpiration}
                  onChange={(e) => handleInputChange('suretyLicenseExpiration', e.target.value)}
                />
              </div>
            </section>

            {/* Office Access */}
            <section className="form-section">
              <h3>Office Access</h3>
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.hasAccessToAllOffices}
                    onChange={(e) => handleInputChange('hasAccessToAllOffices', e.target.checked)}
                  />
                  This user has access to all offices
                </label>
              </div>
              {!formData.hasAccessToAllOffices && (
                <div className="form-group">
                  <label>Select Offices</label>
                  <div className="office-checkboxes">
                    {offices.map(office => (
                      <label key={office.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={formData.officeIds.includes(office.id)}
                          onChange={() => handleOfficeToggle(office.id)}
                        />
                        {office.name}
                      </label>
                    ))}
                  </div>
                </div>
              )}
            </section>

            {/* Address Information */}
            <section className="form-section">
              <h3>Address Information</h3>
              <div className="form-group">
                <label>Address</label>
                <AddressAutocomplete
                  value={formData.addressStreet}
                  onChange={(value) => handleInputChange('addressStreet', value)}
                  onAddressSelect={handleAddressSelect}
                  placeholder="Start typing address..."
                />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input
                    type="text"
                    value={formData.addressCity}
                    onChange={(e) => handleInputChange('addressCity', e.target.value)}
                    placeholder="Enter city"
                  />
                </div>
                <div className="form-group">
                  <label>State</label>
                  <select
                    value={formData.addressState}
                    onChange={(e) => handleInputChange('addressState', e.target.value)}
                  >
                    <option value="MT">Montana</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    {/* Add more states as needed */}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>County</label>
                  <input
                    type="text"
                    value={formData.addressCounty}
                    onChange={(e) => handleInputChange('addressCounty', e.target.value)}
                    placeholder="Enter county"
                  />
                </div>
                <div className="form-group">
                  <label>Zip Code</label>
                  <input
                    type="text"
                    value={formData.addressZip}
                    onChange={(e) => handleInputChange('addressZip', e.target.value)}
                    placeholder="Enter zipcode"
                    maxLength="10"
                  />
                </div>
              </div>
            </section>

            {/* Driver's License */}
            <section className="form-section">
              <h3>Driver's License</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Drivers License #</label>
                  <input
                    type="text"
                    value={formData.driversLicense}
                    onChange={(e) => handleInputChange('driversLicense', e.target.value)}
                    placeholder="Drivers License"
                  />
                </div>
                <div className="form-group">
                  <label>D/L State</label>
                  <select
                    value={formData.driversLicenseState}
                    onChange={(e) => handleInputChange('driversLicenseState', e.target.value)}
                  >
                    <option value="MT">Montana</option>
                    <option value="AL">Alabama</option>
                    <option value="AK">Alaska</option>
                    <option value="AZ">Arizona</option>
                    {/* Add more states as needed */}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>D/L Expiration</label>
                <input
                  type="date"
                  value={formData.driversLicenseExpiration}
                  onChange={(e) => handleInputChange('driversLicenseExpiration', e.target.value)}
                  placeholder="Expiration date"
                />
              </div>
            </section>

            {/* Profile Picture */}
            <section className="form-section">
              <h3>Profile Picture</h3>
              <div className="form-group">
                <label>Upload Profile Picture</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    // Handle file upload - would need to implement file upload to server
                    console.log('File upload not yet implemented');
                  }}
                />
                <small>File upload functionality to be implemented</small>
              </div>
            </section>

            <div className="form-actions">
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
              <button type="submit" className="btn-primary">
                {editingStaff ? 'Update Staff Member' : 'Create Staff Member'}
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="staff-list">
          <table className="staff-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Phone</th>
                <th>Hire Date</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr>
                  <td colSpan="7" className="no-data">
                    No staff members found. Click "Add Staff Member" to get started.
                  </td>
                </tr>
              ) : (
                staff.map(member => (
                  <tr key={member.id}>
                    <td>
                      <div className="staff-name">
                        <FaUser />
                        {member.first_name} {member.last_name}
                      </div>
                    </td>
                    <td>{member.email}</td>
                    <td><span className={`role-badge role-${member.role}`}>{member.role}</span></td>
                    <td>{member.phone || '-'}</td>
                    <td>{member.hire_date ? new Date(member.hire_date).toLocaleDateString() : '-'}</td>
                    <td>
                      <span className={`status-badge ${member.is_active ? 'status-active' : 'status-inactive'}`}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions">
                      <button
                        className="btn-icon btn-edit"
                        onClick={() => handleEdit(member)}
                        title="Edit"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="btn-icon btn-delete"
                        onClick={() => handleDelete(member.id)}
                        title="Deactivate"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default StaffManagement;
